import { useState } from 'react';
import confetti from 'canvas-confetti';
import { selectPrize, recordWin } from './gameUtils';

const MysteryBox = ({ clientId, prizes, setVoucher, setScreen }) => {
  const [gameKey, setGameKey] = useState(0);
  return <MysteryBoxInner key={gameKey} clientId={clientId} prizes={prizes}
    setVoucher={setVoucher} setScreen={setScreen} onRetry={() => setGameKey(k => k + 1)} />;
};

const MysteryBoxInner = ({ clientId, prizes, setVoucher, setScreen, onRetry }) => {
  const [prize] = useState(() => selectPrize(prizes));
  const [phase, setPhase] = useState('choose');
  const [selected, setSelected] = useState(-1);
  const [showLose, setShowLose] = useState(false);

  const handlePick = async (idx) => {
    if (phase !== 'choose' || !prize) return;
    setSelected(idx);

    setPhase('shake');
    await new Promise(r => setTimeout(r, 2000));
    setPhase('reveal');

    if (!prize.win) {
      setTimeout(() => setShowLose(true), 1500);
    } else {
      setTimeout(() => {
        confetti({ particleCount: 100, spread: 80, colors: ['#0d9488', 'white', '#14b8a6', '#dc2626'], origin: { y: 0.4 } });
      }, 800);
      const data = await recordWin(clientId, prize.id, prize.label);
      if (data.win && data.voucher) {
        setTimeout(() => { setVoucher(data.voucher); setScreen('voucher'); }, 2800);
      }
    }
  };

  const BOX_STYLES = [
    { body: 'linear-gradient(180deg, #1a2d47, #152438)', accent: '#0d9488', glow: 'rgba(201,168,76,0.15)' },
    { body: 'linear-gradient(180deg, #2a1f3d, #1f1530)', accent: '#9B6DFF', glow: 'rgba(155,109,255,0.15)' },
    { body: 'linear-gradient(180deg, #1d3532, #152a28)', accent: '#00D4AA', glow: 'rgba(0,212,170,0.15)' },
  ];

  if (!prize) return null;

  return (
    <div className="text-center" data-testid="coffre-screen">
      <style>{`
        @keyframes coffre-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes coffre-shake {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-6px) rotate(-2deg); }
          20%, 40%, 60%, 80% { transform: translateX(6px) rotate(2deg); }
        }
        @keyframes prize-pop {
          0% { transform: scale(0) rotate(-15deg); opacity: 0; }
          50% { transform: scale(1.2) rotate(5deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes lid-glow {
          0%, 100% { filter: brightness(1); }
          50% { filter: brightness(1.3); }
        }
      `}</style>

      <h2 className="text-2xl font-bold text-[#0d9488] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Le Coffre Mystere
      </h2>
      <p className="text-[white]/50 text-sm mb-8">
        {phase === 'choose' ? 'Choisissez un coffre pour decouvrir votre lot !' :
          phase === 'shake' ? 'Les coffres tremblent...' : 'Ouverture...'}
      </p>

      <div className="flex justify-center gap-5">
        {[0, 1, 2].map(i => {
          const style = BOX_STYLES[i];
          const isSelected = selected === i;
          const isHidden = phase === 'reveal' && !isSelected;
          const isOpen = phase === 'reveal' && isSelected;
          const isShaking = phase === 'shake';

          return (
            <button key={i} onClick={() => handlePick(i)} disabled={phase !== 'choose'}
              className="relative focus:outline-none"
              style={{
                width: 95, height: 115,
                opacity: isHidden ? 0 : 1,
                transform: isHidden ? 'scale(0.5)' : isOpen ? 'scale(1.15)' : 'scale(1)',
                transition: 'opacity 0.7s ease, transform 0.7s ease',
                animation: phase === 'choose' ? `coffre-float ${2.5 + i * 0.4}s ease-in-out infinite` :
                  isShaking ? 'coffre-shake 0.4s ease-in-out infinite' : 'none',
                cursor: phase === 'choose' ? 'pointer' : 'default',
              }}
              data-testid={`coffre-${i}`}>

              {/* Box body */}
              <div className="absolute bottom-0 left-0 right-0 rounded-lg overflow-hidden"
                style={{ height: '62%', background: style.body, border: `2px solid ${style.accent}40`,
                  boxShadow: `0 0 20px ${style.glow}` }}>
                {/* Metal bands */}
                <div className="absolute left-0 right-0 top-[20%] h-[2px]" style={{ background: style.accent + '30' }} />
                <div className="absolute left-0 right-0 bottom-[20%] h-[2px]" style={{ background: style.accent + '30' }} />
                {/* Keyhole */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 rounded-full border-2" style={{ borderColor: style.accent + '60' }} />
                  <div className="w-1.5 h-3 mx-auto -mt-0.5 rounded-b" style={{ background: style.accent + '40' }} />
                </div>
              </div>

              {/* Lid */}
              <div className="absolute left-[-6%] right-[-6%] rounded-t-xl"
                style={{
                  height: '42%',
                  top: isOpen ? '-35%' : '0%',
                  opacity: isOpen ? 0 : 1,
                  transition: 'top 0.8s ease, opacity 0.6s ease 0.3s',
                  background: `linear-gradient(180deg, ${style.accent}, ${style.accent}aa)`,
                  borderBottom: `3px solid ${style.accent}`,
                  animation: phase === 'choose' ? 'lid-glow 3s ease-in-out infinite' : 'none',
                }}>
                <div className="absolute inset-x-0 top-0 h-1/3 rounded-t-xl"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.2), transparent)' }} />
              </div>

              {/* Prize reveal */}
              {isOpen && (
                <div className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{ animation: 'prize-pop 0.6s ease-out forwards' }}>
                  <span className="text-3xl">{prize.emoji}</span>
                  <p className={`text-[10px] font-bold mt-1 leading-tight px-1 ${prize.win ? 'text-[#0d9488]' : 'text-[white]/40'}`}
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {prize.win ? prize.label : 'Vide !'}
                  </p>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {phase === 'reveal' && prize?.win && (
        <p className="text-[#0d9488] text-sm mt-8 animate-pulse" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Preparation de votre bon...
        </p>
      )}

      {showLose && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" data-testid="lose-dialog">
          <div className="bg-[#243559] border border-[#0d9488]/20 rounded-2xl p-8 max-w-sm w-full text-center">
            <p className="text-3xl mb-3">&#128532;</p>
            <h3 className="text-xl text-[white] font-semibold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Pas de chance !
            </h3>
            <p className="text-[white]/60 text-sm mb-6">Voulez-vous retenter votre chance ?</p>
            <div className="flex gap-3">
              <button onClick={() => setScreen('register')}
                className="flex-1 py-2.5 rounded-lg border border-[white]/20 text-[white]/70 text-sm hover:bg-[white]/5 transition"
                data-testid="lose-quit">Non merci</button>
              <button onClick={onRetry}
                className="flex-1 py-2.5 rounded-lg bg-[#0d9488] text-[#1a2744] font-bold text-sm hover:bg-[#14b8a6] transition"
                data-testid="lose-retry">Retenter !</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MysteryBox;
